/**
 * Display helpers for user identity: name casing, avatar initials, and
 * deterministic avatar colors. Keycloak / Azure AD often federates names in
 * inconsistent casing (e.g. "anneliese.khalil"), so these normalize what the
 * UI renders without mutating the underlying record.
 */

/**
 * Title-cases a name for display: lowercases everything, then capitalizes the
 * first letter of each whitespace- or hyphen-separated token.
 * e.g. "anneliese" -> "Anneliese", "mary-jane" -> "Mary-Jane".
 */
export const toTitleCase = (value?: string): string =>
  (value ?? '')
    .toLowerCase()
    .replace(/(^|[\s-])(\p{L})/gu, (_match, sep: string, ch: string) => sep + ch.toUpperCase());

/**
 * Returns up to two uppercased initials for an avatar. Prefers first/last name,
 * falling back to the email local part when names are missing.
 */
export const getInitials = (
  firstName?: string,
  lastName?: string,
  email?: string,
): string => {
  const first = firstName?.trim();
  const last = lastName?.trim();
  if (first || last) {
    const initials = `${first?.[0] ?? ''}${last?.[0] ?? ''}`;
    if (initials) return initials.toUpperCase();
  }

  const local = email?.split('@')[0] ?? '';
  const parts = local.split(/[._-]/).filter(Boolean);
  const fallback =
    parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}` : local.slice(0, 2);
  return fallback.toUpperCase() || '?';
};

// Colorful, accessible avatar backgrounds (white text). Kept vivid so avatars
// read as identity chips rather than the default greyed-out container color.
const AVATAR_COLORS = [
  '#2E7D32', // green
  '#1565C0', // blue
  '#E65100', // orange
  '#6A1B9A', // purple
  '#00695C', // teal
  '#C62828', // red
  '#283593', // indigo
  '#AD1457', // pink
  '#00838F', // cyan
  '#4E342E', // brown
];

/**
 * Deterministically maps a seed (typically the user id or email) to a colorful
 * avatar background so a given user always renders the same color.
 */
export const getAvatarColor = (
  seed: string,
): { backgroundColor: string; color: string } => {
  const hash = [...(seed || '')].reduce(
    (acc, char) => acc + char.charCodeAt(0),
    0,
  );
  return {
    backgroundColor: AVATAR_COLORS[hash % AVATAR_COLORS.length],
    color: '#ffffff',
  };
};
